import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { toLower, unionBy, intersection } from 'lodash';
import { TreeSelect as AntdTreeSelect } from 'antd';

import styles from './TagsTreeSelector.less';
import { useContentTags } from 'lib/content-tags';
import { TreeSelect } from '../Select/TreeSelect';

const { SHOW_PARENT } = AntdTreeSelect;

const TagsTreeSelector = (props) => {
  const {
    treeData,
    value,
    onChange,
    onSelect,
    isLoading,
    checkParentNode,
    searchPlaceholder,
    multiple,
    showSearch,
    initialValue,
    disabled,
    className,
    selectProps,
  } = props;

  const filterTreeNode = (string, node) => {
    return toLower(node?.props?.displayTitle ?? node?.props?.title).includes(toLower(string));
  };

  const { contentTagsMapById: treeMap, contentTagsTree } = useContentTags();

  // This could take some time to read
  // Expected behavior: Whenever a leaf is selected, all the parents up to the root gets selected too
  // You cannot deselect a node if one of the leaf is selected. You'll have to deselect
  // all leaf to deselect that node.
  const onChangeWithParent = (changedValue, _, { triggerValue: selectedTagId }) => {
    // When the tag array is cleared - we don't have to check
    if (!changedValue.length) return onChange([]);

    // Detects if there's children that is still selected. If true, disallow by return early
    if (
      intersection(
        treeMap[selectedTagId].children && treeMap[selectedTagId].children.map((tag) => tag._id),
        value.map((tag) => tag.value)
      ).length > 0
    ) {
      return;
    }

    // Normal deselecting
    if (changedValue.length < value.length) return onChange(changedValue);

    // Returns an array of the node and its parents
    function addParentNodeToChecked(nodeId) {
      const parentId = treeMap[nodeId].parentId;
      return parentId ? [treeMap[parentId], ...addParentNodeToChecked(parentId)] : [];
    }

    if (selectedTagId) {
      const tagsWithParents = addParentNodeToChecked(selectedTagId).map((tag) => ({
        label: tag.displayTitle ?? tag.title,
        value: tag._id,
      }));

      onChange(unionBy(changedValue, tagsWithParents, 'value'));
    }
  };

  const tProps = {
    ...props,
    selectProps,
    treeData: treeData || contentTagsTree,
    disabled: disabled || isLoading,
    loading: isLoading,
    value,
    onChange: checkParentNode ? onChangeWithParent : onChange,
    onSelect,
    filterTreeNode,
    treeCheckStrictly: multiple,
    showSearch,
    treeCheckable: multiple,
    showCheckedStrategy: SHOW_PARENT,
    searchPlaceholder,
    className: classnames(styles.treeSelector, className),
    dropdownClassName: styles.treeSelectorDropdown,
    dropdownMatchSelectWidth: false,
    multiple,
    defaultValue: initialValue,
    fieldNames: { label: 'displayTitle' },
    getSelectedLabel: (fullPath) => fullPath.map(({ displayTitle }) => displayTitle).join(' / '),
  };

  if (multiple) {
    return <AntdTreeSelect {...tProps} />;
  }

  return <TreeSelect {...tProps} />;
};

TagsTreeSelector.propTypes = {
  treeData: PropTypes.array.isRequired,
  value: PropTypes.array,
  initialValue: PropTypes.array,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  checkParentNode: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  multiple: PropTypes.bool,
  showSearch: PropTypes.bool,
  allowClear: PropTypes.bool,
  disabled: PropTypes.bool,
  selectProps: PropTypes.object,
};

TagsTreeSelector.defaultProps = {
  onSelect: () => {},
  onChange: () => {},
  value: [],
  isLoading: false,
  checkParentNode: false,
  searchPlaceholder: 'Filter by tag',
  multiple: true,
  showSearch: true,
  allowClear: true,
  initialValue: [],
  disable: false,
};

export default TagsTreeSelector;
